import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Button, Chip, CircularProgress, Alert, Grid, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import api from '../utils/axiosConfig';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4), minHeight: '100vh',
  background: 'linear-gradient(135deg, #121212 0%, #2C2C2C 100%)',
  [theme.breakpoints.up('md')]: { maxWidth: '1000px' }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4), borderRadius: '16px',
  background: 'linear-gradient(135deg, #2D2D2D 0%, #1A1A1A 100%)',
  border: '1px solid rgba(255, 215, 0, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', marginBottom: theme.spacing(3)
}));

const label = { color: '#FFD700', fontWeight: 600, mb: 0.5 };
const val = { color: 'rgba(255,255,255,0.85)', mb: 2 };

const EmployerJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/jobs/${id}`)
      .then(res => setJob(res.data))
      .catch(() => setError('Failed to load job'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: '#FFD700' }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!job) return <Box sx={{ p: 4 }}><Alert severity="warning">Job not found</Alert></Box>;

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #121212 0%, #2C2C2C 100%)', py: 4 }}>
      <StyledContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/employer/jobs')} sx={{ color: '#FFD700' }}>Back to Jobs</Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/employer/jobs/${id}/edit`)}
            sx={{ background: 'linear-gradient(90deg, #FFD700, #009639)', color: '#000', fontWeight: 600, textTransform: 'none' }}>
            Edit Job
          </Button>
        </Box>

        <StyledPaper>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <WorkIcon sx={{ color: '#FFD700', fontSize: '2rem' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 'bold' }}>{job.title}</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {job.location && <Chip icon={<LocationOnIcon />} label={job.location} size="small" sx={{ color: '#fff', bgcolor: 'rgba(255,215,0,0.15)' }} />}
                {job.type && <Chip label={job.type.replace('_', ' ')} size="small" sx={{ color: '#fff', bgcolor: 'rgba(0,150,57,0.3)' }} />}
                <Chip label={job.status || 'ACTIVE'} size="small" color={job.status === 'ACTIVE' ? 'success' : 'warning'} />
              </Box>
            </Box>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,215,0,0.2)', mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={label}>Posted</Typography>
              <Typography sx={val}>{new Date(job.createdAt).toLocaleDateString()}</Typography>
            </Grid>
            {job.experience && <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={label}>Experience</Typography>
              <Typography sx={val}>{job.experience}</Typography>
            </Grid>}
            {job.salary && <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={label}>Salary</Typography>
              <Typography sx={val}>{typeof job.salary === 'object' ? `${job.salary.min} - ${job.salary.max}` : job.salary}</Typography>
            </Grid>}
            {job.company?.name && <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={label}>Company</Typography>
              <Typography sx={val}>{job.company.name}</Typography>
            </Grid>}
          </Grid>

          <Typography variant="subtitle2" sx={{ ...label, mt: 2 }}>Description</Typography>
          <Typography sx={{ ...val, whiteSpace: 'pre-wrap' }}>{job.description}</Typography>

          {job.skills?.length > 0 && <>
            <Typography variant="subtitle2" sx={label}>Skills</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {job.skills.map((s, i) => <Chip key={i} label={s} size="small" sx={{ color: '#fff', bgcolor: 'rgba(255,215,0,0.2)' }} />)}
            </Box>
          </>}
        </StyledPaper>
      </StyledContainer>
    </Box>
  );
};

export default EmployerJobDetail;
